import React, { useCallback, useRef, useState } from "react";
import {
    AlertTriangle,
    Car,
    CheckCircle2,
    FileDown,
    FileText,
    Loader2,
    Scale,
    ScanLine,
    Sparkles,
    Upload,
    Wrench,
    XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import DkPdfSablonMenu from "./DkPdfSablonMenu";

function formatHata(error) {
    if (!error) return "Bilinmeyen hata";
    if (Array.isArray(error)) return error.map(String).join(" ");
    return String(error);
}

const STEPS = [
    { id: "upload", label: "PDF yükle" },
    { id: "parse", label: "Parça tespiti" },
    { id: "calc", label: "DK hesabı" },
    { id: "done", label: "Sonuç" },
];

const KATEGORI_STIL = {
    plastik: "bg-cyan-500/10 border-cyan-500/30 text-cyan-300",
    cam: "bg-sky-500/10 border-sky-500/30 text-sky-300",
    aydinlatma: "bg-amber-500/10 border-amber-500/30 text-amber-300",
    kaporta: "bg-slate-500/10 border-slate-500/30 text-slate-300",
};

function StepBar({ activeIdx }) {
    return (
        <div className="flex flex-wrap items-center gap-1 sm:gap-2">
            {STEPS.map((s, i) => {
                const done = i < activeIdx;
                const active = i === activeIdx;
                return (
                    <React.Fragment key={s.id}>
                        <div
                            className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] border transition-all ${
                                active
                                    ? "border-emerald-400/60 bg-emerald-500/15 text-emerald-200"
                                    : done
                                      ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-400/80"
                                      : "border-white/10 text-slate-600"
                            }`}
                        >
                            <span
                                className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
                                    done ? "bg-emerald-500/30" : active ? "bg-emerald-500/40" : "bg-white/5"
                                }`}
                            >
                                {done ? "✓" : i + 1}
                            </span>
                            {s.label}
                        </div>
                        {i < STEPS.length - 1 ? (
                            <div className={`hidden sm:block w-4 h-px ${done ? "bg-emerald-500/40" : "bg-white/10"}`} />
                        ) : null}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

function KategoriBadge({ kategori }) {
    if (!kategori) return null;
    const stil = KATEGORI_STIL[kategori] || KATEGORI_STIL.kaporta;
    return (
        <span className={`text-[9px] px-1.5 py-0.5 rounded border capitalize ${stil}`}>
            {kategori}
        </span>
    );
}

function ParcaRow({ p, detayli = false }) {
    return (
        <div
            className={`flex items-start gap-3 px-3 py-2 rounded-lg border text-xs ${
                p.eslesti
                    ? "border-emerald-500/20 bg-emerald-500/5"
                    : "border-amber-500/20 bg-amber-500/5"
            }`}
        >
            {p.eslesti ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
            ) : (
                <XCircle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-white font-medium truncate">{p.parca_adi}</span>
                    <KategoriBadge kategori={p.kategori} />
                </div>
                <div className="text-slate-500 capitalize mt-0.5">
                    {(p.islem_tipi || "onarim").replace(/_/g, " ")}
                    {p.diagram_etiket ? ` · ${p.diagram_etiket}` : " · eşleşmedi"}
                </div>
                {detayli && (p.parca_fiyat_fmt || p.iscilik_fmt) ? (
                    <div className="flex flex-wrap gap-2 mt-1 text-[10px] text-slate-500">
                        {p.parca_fiyat_fmt ? <span>Parça: {p.parca_fiyat_fmt}</span> : null}
                        {p.iscilik_fmt ? <span>İşçilik: {p.iscilik_fmt}</span> : null}
                    </div>
                ) : null}
            </div>
            {p.tutar_fmt ? (
                <span className="text-slate-300 font-mono shrink-0 font-medium">{p.tutar_fmt}</span>
            ) : null}
        </div>
    );
}

function RapordanAlanlar({ rapor, rayicOtomatik }) {
    if (!rapor?.success) return null;
    const alanlar = [
        { label: "Marka", value: rapor.marka },
        { label: "Model", value: rapor.model },
        { label: "Model yılı", value: rapor.model_yili },
        { label: "KM", value: rapor.km != null ? `${Number(rapor.km).toLocaleString("tr-TR")} km` : null },
        {
            label: "Rayiç değer",
            value: rapor.rayic_deger_fmt,
            vurgu: true,
            alt: rapor.rayic_kaynak === "ai" ? "AI tamamlama" : rapor.rayic_kaynak === "rapor" ? "Ekspertiz raporu" : rayicOtomatik ? "Piyasa araştırması" : null,
        },
        { label: "Parça toplam", value: rapor.parca_toplam ? formatTl(rapor.parca_toplam) : null },
        { label: "İşçilik toplam", value: rapor.iscilik_toplam ? formatTl(rapor.iscilik_toplam) : null },
        { label: "Hasar tutarı", value: rapor.hasar_tutari_fmt, vurgu: true },
        { label: "Bilirkişi NET DK", value: rapor.net_deger_kaybi_fmt, altin: true },
    ].filter((a) => a.value);

    if (!alanlar.length) return null;

    return (
        <div className="rounded-xl border border-white/10 bg-black/25 p-4">
            <h4 className="text-xs font-semibold text-white flex items-center gap-2 mb-3">
                <Car className="h-3.5 w-3.5 text-violet-400" />
                Rapordan okunan değerler
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {alanlar.map((a) => (
                    <div
                        key={a.label}
                        className={`rounded-lg p-2.5 border ${
                            a.altin
                                ? "border-[#D4AF37]/30 bg-[#D4AF37]/5"
                                : a.vurgu
                                  ? "border-emerald-500/25 bg-emerald-500/5"
                                  : "border-white/5 bg-white/[0.02]"
                        }`}
                    >
                        <div className="text-[9px] uppercase tracking-wider text-slate-500">{a.label}</div>
                        <div
                            className={`text-sm font-medium mt-0.5 ${
                                a.altin ? "text-[#D4AF37]" : a.vurgu ? "text-emerald-300" : "text-slate-200"
                            }`}
                        >
                            {a.value}
                        </div>
                        {a.alt ? <div className="text-[9px] text-slate-600 mt-0.5">{a.alt}</div> : null}
                    </div>
                ))}
            </div>
            {rapor.eksik_alanlar?.length ? (
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {rapor.eksik_alanlar.map((e) => (
                        <span
                            key={e}
                            className="text-[9px] px-2 py-0.5 rounded-full bg-rose-500/10 border border-rose-500/25 text-rose-300"
                        >
                            Eksik: {e}
                        </span>
                    ))}
                </div>
            ) : null}
            {rapor.tespit_ozeti ? (
                <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">{rapor.tespit_ozeti}</p>
            ) : null}
        </div>
    );
}

function formatTl(val) {
    if (val == null) return null;
    return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(val);
}

export default function DkEkspertizRaporPanel({
    loading,
    onAnaliz,
    sonuc,
    onTemizle,
    seciliDosya,
    onFaturaOcr,
    onPdf,
    pdfLoading,
}) {
    const inputRef = useRef(null);
    const [drag, setDrag] = useState(false);
    const [dosyaAdi, setDosyaAdi] = useState(null);

    const stepIdx = loading ? 1 : sonuc?.success ? 3 : sonuc ? 2 : 0;
    const rapor = sonuc?.rapor;

    const handleFile = useCallback(
        (file) => {
            if (!file) return;
            const ok =
                file.type === "application/pdf" ||
                file.type.startsWith("image/") ||
                /\.(pdf|jpg|jpeg|png|webp)$/i.test(file.name);
            if (!ok) return;
            setDosyaAdi(file.name);
            onAnaliz(file);
        },
        [onAnaliz],
    );

    const onDrop = (e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer.files?.[0];
        handleFile(f);
    };

    return (
        <div className="relative overflow-hidden rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-[#0a1628] via-[#0c1222] to-[#060a12]">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_20%_0%,rgba(16,185,129,0.12),transparent)] pointer-events-none" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-[radial-gradient(circle,rgba(212,175,55,0.06),transparent_70%)] pointer-events-none" />

            <div className="relative p-5 md:p-6 space-y-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[10px] uppercase tracking-wider text-emerald-300 mb-2">
                            <ScanLine className="h-3 w-3" />
                            Ekspertiz AI
                        </div>
                        <h2 className="text-lg md:text-xl font-semibold text-white tracking-tight">
                            Ekspertiz Raporu → Gelişmiş Değer Kaybı
                        </h2>
                        <p className="text-xs text-slate-400 mt-1 max-w-xl leading-relaxed">
                            Rayiç, marka/model, parça fiyatları, işçilikler ve plastik/cam/aydınlatma parçaları
                            ekspertiz raporundan okunur. MBS gelişmiş hesap bilirkişi NET DK ile karşılaştırılır.
                        </p>
                    </div>
                    <StepBar activeIdx={stepIdx} />
                </div>

                {!sonuc?.success ? (
                    <div
                        role="button"
                        tabIndex={0}
                        onDragOver={(e) => {
                            e.preventDefault();
                            setDrag(true);
                        }}
                        onDragLeave={() => setDrag(false)}
                        onDrop={onDrop}
                        onClick={() => !loading && inputRef.current?.click()}
                        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
                        className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-all duration-300 p-8 md:p-10 text-center ${
                            drag
                                ? "border-emerald-400 bg-emerald-500/10 scale-[1.01]"
                                : loading
                                  ? "border-emerald-500/30 bg-black/30 cursor-wait"
                                  : "border-white/15 hover:border-emerald-500/40 hover:bg-white/[0.02]"
                        }`}
                    >
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".pdf,application/pdf,image/*"
                            className="hidden"
                            disabled={loading}
                            onChange={(e) => {
                                handleFile(e.target.files?.[0]);
                                e.target.value = "";
                            }}
                        />
                        <div className="flex flex-col items-center gap-3">
                            {loading ? (
                                <>
                                    <Loader2 className="h-12 w-12 text-emerald-400 animate-spin" />
                                    <div className="text-sm text-emerald-200 font-medium">
                                        {dosyaAdi ? `"${dosyaAdi}" analiz ediliyor…` : "Analiz ediliyor…"}
                                    </div>
                                    <p className="text-[11px] text-slate-500 max-w-sm">
                                        Rayiç, parça tablosu, plastik/cam/far tespiti ve DK hesabı yapılıyor.
                                        45–120 sn sürebilir.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center">
                                        <Upload className="h-8 w-8 text-emerald-400" />
                                    </div>
                                    <div>
                                        <div className="text-sm text-white font-medium">
                                            Ekspertiz PDF sürükleyin veya tıklayın
                                        </div>
                                        <div className="text-[11px] text-slate-500 mt-1">
                                            PDF · JPG · PNG · max 20 MB
                                        </div>
                                    </div>
                                    {seciliDosya ? (
                                        <div className="text-[10px] text-violet-300/90 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20">
                                            Dosya bağlamı: {seciliDosya.dosya_no} · {seciliDosya.plaka}
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </div>
                    </div>
                ) : null}

                {sonuc && !sonuc.success ? (
                    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
                        <div className="flex gap-2 text-sm text-amber-200">
                            <AlertTriangle className="h-5 w-5 shrink-0" />
                            <div>
                                <div className="font-medium">Tam otomatik hesap tamamlanamadı</div>
                                <p className="text-xs text-amber-200/80 mt-1">{formatHata(sonuc.error)}</p>
                            </div>
                        </div>
                        {rapor ? <RapordanAlanlar rapor={rapor} rayicOtomatik={sonuc.rayic_otomatik} /> : null}
                        {sonuc.parca_ozet?.parcalar?.length ? (
                            <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                {sonuc.parca_ozet.parcalar.map((p, i) => (
                                    <ParcaRow key={i} p={p} detayli />
                                ))}
                            </div>
                        ) : null}
                        <div className="flex gap-2">
                            <Button type="button" size="sm" variant="outline" className="border-white/10" onClick={onTemizle}>
                                Yeni rapor
                            </Button>
                            {sonuc.gelismis_form ? (
                                <Button
                                    type="button"
                                    size="sm"
                                    className="bg-emerald-600/80"
                                    onClick={() => onAnaliz(null, { formOnly: sonuc })}
                                >
                                    Formu uygula (manuel hesap)
                                </Button>
                            ) : null}
                        </div>
                    </div>
                ) : null}

                {sonuc?.success ? (
                    <div className="space-y-4 animate-in fade-in duration-500">
                        <RapordanAlanlar rapor={rapor} rayicOtomatik={sonuc.rayic_otomatik} />

                        <div className="grid md:grid-cols-[1fr_auto] gap-4 items-start">
                            <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/40 p-4">
                                <div className="flex items-start gap-3">
                                    <Sparkles className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-emerald-400/80 mb-1">
                                            MBS gelişmiş hesap özeti
                                        </div>
                                        <p className="text-sm text-slate-200 leading-relaxed">{sonuc.aciklama?.ozet}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="rounded-xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-4 text-center min-w-[200px]">
                                <div className="text-[10px] uppercase tracking-wider text-[#D4AF37]/80">
                                    MBS önerilen NET DK
                                </div>
                                <div className="font-display text-3xl text-transparent bg-clip-text bg-gradient-to-r from-emerald-200 to-teal-300 mt-1">
                                    {sonuc.aciklama?.net_deger_kaybi_fmt || sonuc.hesap?.gercek_deger_kaybi_fmt}
                                </div>
                                {sonuc.aciklama?.rayic_fmt ? (
                                    <div className="text-[10px] text-slate-500 mt-2">
                                        Rayiç {sonuc.aciklama.rayic_fmt}
                                        {sonuc.rayic_otomatik ? " · piyasa araştırması" : rapor?.rayic_kaynak === "rapor" ? " · rapordan" : ""}
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        {sonuc.ocr?.en_iyi_motor ? (
                            <div className="flex flex-wrap gap-2 text-[10px]">
                                <span className="px-2 py-1 rounded-full bg-violet-500/10 border border-violet-500/25 text-violet-300">
                                    OCR: {sonuc.ocr.en_iyi_motor}
                                    {sonuc.ocr.guven != null ? ` · güven ${sonuc.ocr.guven}/100` : ""}
                                </span>
                                {sonuc.parse_kaynak ? (
                                    <span className="px-2 py-1 rounded-full bg-slate-500/10 border border-white/10 text-slate-400">
                                        Parse: {sonuc.parse_kaynak}
                                    </span>
                                ) : null}
                                {rapor?.eslesen_parca != null ? (
                                    <span className="px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-300">
                                        {rapor.eslesen_parca}/{rapor.parca_sayisi} parça diyagram eşleşmesi
                                    </span>
                                ) : null}
                            </div>
                        ) : null}

                        {sonuc.karsilastirma?.karsilastirma_yapildi ? (
                            <div className="rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-4">
                                <h4 className="text-xs font-semibold text-[#D4AF37] flex items-center gap-2 mb-3">
                                    <Scale className="h-4 w-4" />
                                    Bilirkişi raporu vs MBS gelişmiş hesap
                                </h4>
                                <div className="grid sm:grid-cols-3 gap-3 text-center">
                                    <div className="rounded-lg bg-black/30 p-3 border border-white/5">
                                        <div className="text-[10px] text-slate-500">Bilirkişi NET DK</div>
                                        <div className="text-lg text-[#D4AF37] font-medium">
                                            {sonuc.karsilastirma.bilirkisi_net_fmt}
                                        </div>
                                    </div>
                                    <div className="rounded-lg bg-black/30 p-3 border border-emerald-500/20">
                                        <div className="text-[10px] text-slate-500">MBS gelişmiş</div>
                                        <div className="text-lg text-emerald-300 font-medium">
                                            {sonuc.karsilastirma.mbs_net_fmt}
                                        </div>
                                    </div>
                                    <div className="rounded-lg bg-black/30 p-3 border border-white/5">
                                        <div className="text-[10px] text-slate-500">Fark</div>
                                        <div
                                            className={`text-lg font-medium ${
                                                sonuc.karsilastirma.uyum === "iyi"
                                                    ? "text-emerald-400"
                                                    : sonuc.karsilastirma.uyum === "orta"
                                                      ? "text-amber-400"
                                                      : "text-rose-400"
                                            }`}
                                        >
                                            {sonuc.karsilastirma.fark_fmt}
                                            {sonuc.karsilastirma.fark_yuzde != null
                                                ? ` (%${Math.abs(sonuc.karsilastirma.fark_yuzde)})`
                                                : ""}
                                        </div>
                                    </div>
                                </div>
                                {sonuc.karsilastirma.notlar?.length ? (
                                    <ul className="mt-3 space-y-1 text-[11px] text-amber-200/80">
                                        {sonuc.karsilastirma.notlar.map((n, i) => (
                                            <li key={i}>• {n}</li>
                                        ))}
                                    </ul>
                                ) : null}
                                {sonuc.hesap?.bilirkisi_uyum_yeniden_hesap ? (
                                    <p className="text-[10px] text-emerald-400/90 mt-2">
                                        Rapordaki rayiç/hasar parametreleriyle otomatik yeniden hesaplandı.
                                    </p>
                                ) : null}
                            </div>
                        ) : sonuc.bilirkisi?.net_deger_kaybi_fmt ? (
                            <div className="rounded-xl border border-[#D4AF37]/20 bg-black/20 p-3 text-xs text-slate-400">
                                Raporda bilirkişi NET DK:{" "}
                                <span className="text-[#D4AF37]">{sonuc.bilirkisi.net_deger_kaybi_fmt}</span>
                            </div>
                        ) : null}

                        {sonuc.aciklama?.uyarilar?.length ? (
                            <div className="flex flex-wrap gap-2">
                                {sonuc.aciklama.uyarilar.map((u, i) => (
                                    <span
                                        key={i}
                                        className="text-[10px] px-2 py-1 rounded-full bg-amber-500/10 border border-amber-500/25 text-amber-200"
                                    >
                                        {u}
                                    </span>
                                ))}
                            </div>
                        ) : null}

                        <div className="grid lg:grid-cols-2 gap-4">
                            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                                <h4 className="text-xs font-semibold text-white flex items-center gap-2 mb-3">
                                    <Wrench className="h-3.5 w-3.5 text-emerald-400" />
                                    Hasar parçaları ({sonuc.parca_ozet?.eslesen}/{sonuc.parca_ozet?.toplam})
                                </h4>
                                <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                                    {(sonuc.parca_ozet?.parcalar || []).map((p, i) => (
                                        <ParcaRow key={i} p={p} detayli />
                                    ))}
                                </div>
                            </div>

                            {sonuc.aciklama?.adimlar?.length ? (
                                <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                                    <h4 className="text-xs font-semibold text-white flex items-center gap-2 mb-3">
                                        <FileText className="h-3.5 w-3.5 text-emerald-400" />
                                        Hesap adımları
                                    </h4>
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                        {sonuc.aciklama.adimlar.map((a, i) => (
                                            <div
                                                key={i}
                                                className="flex justify-between gap-2 text-xs border-l-2 border-emerald-500/30 pl-3 py-0.5"
                                            >
                                                <span className="text-slate-400">{a.baslik}</span>
                                                <span className="text-emerald-300 font-mono shrink-0">{a.deger}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <div className="flex flex-wrap gap-2 pt-1 items-center">
                            {onPdf ? (
                                <DkPdfSablonMenu
                                    compact
                                    disabled={pdfLoading}
                                    loading={pdfLoading}
                                    onIndir={onPdf}
                                />
                            ) : null}
                            <Button type="button" size="sm" variant="outline" className="border-white/10" onClick={onTemizle}>
                                Yeni ekspertiz raporu
                            </Button>
                        </div>
                    </div>
                ) : null}

                <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-white/5">
                    <span className="text-[10px] text-slate-600">Hasar faturası için klasik OCR</span>
                    <button
                        type="button"
                        disabled={loading}
                        onClick={onFaturaOcr}
                        className="text-[10px] text-violet-400 hover:text-violet-300 underline-offset-2 hover:underline disabled:opacity-50"
                    >
                        Fatura / tutanak yükle →
                    </button>
                </div>
            </div>
        </div>
    );
}
